// A past month's portfolio: every job's figures, computed from the raw as-of model.
//
// The server sends each job's model exactly as the job's own tab gets it for that month, and
// the figures are derived HERE with computeRows/totals/summarize, the same functions the tab
// and Forecast History use. Deriving them on the server, or restating the formulas, would give
// the portfolio a second implementation that could disagree with the job it links to (the
// reason lib/wipHistory.ts is built the same way).
//
// The live portfolio is the exception: its server roll-up is a single pass over every job and
// stays as it is. This file is only the month view and its export.
import { computeRows, totals, summarize, type WipRow } from './wip';
import type { PortfolioAsOf, PortfolioAsOfJob, PortfolioWipJob } from './apiClient';

export interface MonthJob {
  /** The same row shape the live portfolio table renders. */
  figures: PortfolioWipJob;
  /** The job's cost-code rows, recomputed, for the by-code sheet of the export. */
  rows: WipRow[];
  /** Figures Close stored (true) or re-cut from the month-end freeze and the GL (false). */
  final: boolean;
  /** When the month-end freeze behind the contract, estimate and POs was taken. */
  frozenAt: string;
  /** The job had no synced estimate at month end. Nothing measured against the estimate holds. */
  noEstimate: boolean;
  /** The project has been deleted since the month; its frozen figures still count in it. */
  deleted: boolean;
}

/**
 * What a CLOSED month's banner and workbook say about whether its figures can still move.
 *
 * "Final" is true of a job only when Close stored figures for it. A job that had no month-end
 * freeze when the month closed has nothing stored and is re-cut from the GL as it stands now,
 * so it can still change; saying every figure is final over such a job is wrong. One sentence
 * for both places, so they cannot drift apart.
 */
export function closedFinalityNote(total: number, recut: number): string {
  if (recut <= 0) {
    return 'These are the final figures, stored when the month was closed: they no longer change if Intacct does.';
  }
  const stored = total - recut;
  return `${stored} of ${total} job${total === 1 ? '' : 's'} ${stored === 1 ? 'shows' : 'show'} the final figures stored when the month was closed. `
    + `The other ${recut} had nothing stored then, so ${recut === 1 ? 'it is' : 'they are'} re-cut from Intacct as it stands now and can still change.`;
}

/** The query string a link to a job's Financials tab carries to open on a past month. */
export const asOfQuery = (period: string) => `&asOf=${period}`;

/** The month a link asked for (`?asOf=YYYY-MM`), or null: anything that is not a month is ignored. */
export function asOfFromSearch(search: string): string | null {
  const v = new URLSearchParams(search).get('asOf');
  return v && /^\d{4}-(0[1-9]|1[0-2])$/.test(v) ? v : null;
}

/**
 * Margin health, by the rule the live portfolio uses: fading AND underbilled is red, fading
 * alone is orange. (The legend under the table says the same.)
 *
 * ⚠️ MIRRORED in `getPortfolioWip` (apps/api/src/financials/financials.service.ts), which
 * decides the live roll-up's `health` with the same two comparisons. Change both, or neither,
 * or the live view and a month's view color one job differently. This copy's cases are pinned
 * in portfolioMonth.spec; the API's copy has no test of its own.
 */
export function marginHealth(fadeDollars: number, overUnderBilled: number): 'green' | 'orange' | 'red' {
  if (fadeDollars < 0 && overUnderBilled < 0) return 'red';
  return fadeDollars < 0 ? 'orange' : 'green';
}

/** One job's figures for the month, from its raw as-of model. */
export function monthJob(j: PortfolioAsOfJob): MonthJob {
  const m = j.model;
  // A Completed job releases the budget left on codes with no forecast, as on the job's tab;
  // the model carries the flag as it stood in that month.
  const rows = computeRows(m.rows, { ctc: m.forecast, budgetAdj: m.budgetAdj, releaseRemaining: m.jobCompleted === true });
  const t = totals(rows);
  const s = summarize(t, m.contract, m.indirectActual);
  // No synced estimate: the budget is missing, so estimate profit would be the whole contract and
  // fade would read as a catastrophe. The job tab shows those as unavailable; here they are made
  // NEUTRAL (no fade, estimate profit equal to projected) so no total or count is polluted, and
  // `noEstimate` tells the page and the export to show them as missing rather than as numbers.
  const noEstimate = j.budgetSynced === false;
  const estimateProfit = noEstimate ? s.projectedProfit : s.estimateProfit;
  const estimateMarginPct = noEstimate ? s.projectedMarginPct : s.estimateMarginPct;
  const fadeDollars = noEstimate ? 0 : s.fadeDollars;
  const fadePts = noEstimate ? 0 : s.fadePts;
  return {
    final: j.final,
    frozenAt: j.frozenAt,
    noEstimate,
    deleted: j.deleted === true,
    rows,
    figures: {
      projectId: j.projectId, jobNumber: j.jobNumber, title: j.title, division: j.division,
      revised: s.revised, billed: s.billed, budget: s.budget, actual: t.actual, eac: s.eac,
      pctComplete: s.pctComplete, earned: s.earned,
      projectedProfit: s.projectedProfit, projectedMarginPct: s.projectedMarginPct,
      indirectActual: s.indirectActual,
      superProfit: s.superProfit, superMarginPct: s.superMarginPct,
      estimateProfit, estimateMarginPct,
      fadeDollars, fadePts,
      overUnderBilled: s.overUnderBilled, leftToBill: s.leftToBill,
      health: marginHealth(fadeDollars, s.overUnderBilled),
      ...(noEstimate ? { noEstimate: true } : {}),
      ...(j.deleted === true ? { deletedSince: true } : {}),
    },
  };
}

export interface MonthPortfolio {
  /** Jobs with a contract at month end, by job number: what the live table shows. */
  jobs: MonthJob[];
  /** Jobs with a freeze but no contract value at month end. The live portfolio leaves these out
   *  too; they are listed so an export does not drop a job without saying so. */
  noContract: PortfolioAsOfJob[];
}

export function monthPortfolio(data: PortfolioAsOf): MonthPortfolio {
  const jobs: MonthJob[] = [];
  const noContract: PortfolioAsOfJob[] = [];
  for (const j of data.jobs) {
    const mj = monthJob(j);
    if (mj.figures.revised > 0) jobs.push(mj); else noContract.push(j);
  }
  jobs.sort((a, b) => a.figures.jobNumber.localeCompare(b.figures.jobNumber));
  return { jobs, noContract };
}
